import React, { useState, useEffect } from 'react';
import { useSession } from '../../../Utils/SessionContext';
import { usePopupClose } from '../../../Utils/usePopupClose';
import useSkillsAutocomplete from '../../../Utils/useSkillsAutocomplete';
import ComboBoxSelect from '../../../CreateEvaluationComponents/Shards/ComboBoxSelect';
import InputList from '../../../CreateEvaluationComponents/Shards/InputList';
import CodeEditor from '../../Utils/CodeEditor';
import { updateChallengeQuestions } from '../../Utils/QuestionUpdater';
import { questionTypeRequiresCodeSnippet } from '../../Utils/QuestionTemplates';
import toast from 'react-hot-toast';

/**
 * EditChallengeQuestion Component
 * Modal for editing challenge questions
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Close handler
 * @param {object} question - The question object to edit
 * @param {number} questionIndex - Index of the question in the array
 * @param {string} challengeId - Challenge ID for API call
 * @param {array} allQuestions - Full questions array
 * @param {function} onSuccess - Callback after successful save
 */
const EditChallengeQuestion = ({
  isOpen,
  onClose,
  question,
  questionIndex,
  challengeId,
  allQuestions,
  onSuccess
}) => {
  const { user } = useSession();
  const [isSaving, setIsSaving] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  
  // Skills autocomplete hook
  const { skillsOptions, isLoadingSkills, searchSkills, clearSkills } = useSkillsAutocomplete();
  
  // Use popup close hook - close on ESC key only, not on click outside
  const popupRef = usePopupClose(isOpen, onClose, false, true);

  // Handle mounting/unmounting with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready for animation
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  // State for edited question
  const [editedQuestion, setEditedQuestion] = useState({
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    options: [],
    type: '',
    id: '',
    codeSnippet: '',
    statement: '',
    assessmentInstructions: ''
  });

  // Initialize form when question changes
  useEffect(() => {
    if (question) {
      setEditedQuestion({
        questionText: question.questionText || '',
        questionDetails: question.questionDetails || '',
        skillsTested: question.skillsTested || [],
        options: question.options || [],
        type: question.type || '',
        id: question.id || '',
        codeSnippet: question.codeSnippet || '',
        statement: question.statement || '',
        assessmentInstructions: question.assessmentInstructions || ''
      });
    }
  }, [question]);

  // Convert skills array to ComboBoxSelect format
  const skillsAsOptions = (Array.isArray(editedQuestion.skillsTested) 
    ? editedQuestion.skillsTested 
    : typeof editedQuestion.skillsTested === 'string' 
      ? editedQuestion.skillsTested.split(',').map(s => s.trim())
      : []
  ).map(skill => ({
    value: skill,
    label: skill
  }));

  // Convert options array to InputList format
  const optionsAsItems = (editedQuestion.options || []).map(opt => ({
    title: typeof opt === 'string' ? opt : (opt.text || opt),
    subtitle: ''
  }));

  // Handle skills change
  const handleSkillsChange = (selectedOptions) => {
    const skills = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setEditedQuestion({ ...editedQuestion, skillsTested: skills });
  };

  // Handle creating new skill
  const handleCreateSkill = (inputValue) => {
    const currentSkills = Array.isArray(editedQuestion.skillsTested) 
      ? editedQuestion.skillsTested 
      : [];
    const newSkills = [...currentSkills, inputValue];
    setEditedQuestion({ ...editedQuestion, skillsTested: newSkills });
  };

  // Handle options change
  const handleOptionsChange = (items) => {
    const options = items.map(item => item.title);
    setEditedQuestion({ ...editedQuestion, options });
  };

  // Handle question text change
  const handleQuestionTextChange = (e) => {
    setEditedQuestion({ ...editedQuestion, questionText: e.target.value });
  };

  // Handle question details change
  const handleQuestionDetailsChange = (e) => {
    setEditedQuestion({ ...editedQuestion, questionDetails: e.target.value });
  };

  // Handle statement change (for true-false questions)
  const handleStatementChange = (e) => {
    setEditedQuestion({ ...editedQuestion, statement: e.target.value });
  };

  // Handle code snippet change
  const handleCodeSnippetChange = (value) => {
    setEditedQuestion({ ...editedQuestion, codeSnippet: value });
  };

  // Handle assessment instructions change
  const handleAssessmentInstructionsChange = (e) => {
    setEditedQuestion({ ...editedQuestion, assessmentInstructions: e.target.value });
  };

  // Validate form
  const validateForm = () => {
    if (!editedQuestion.questionText.trim()) {
      toast.error('Question text is required');
      return false;
    }

    // Validate options for choice questions
    if (['multiple-answer', 'single-answer', 'true-false'].includes(editedQuestion.type)) {
      if (!editedQuestion.options || editedQuestion.options.length < 2) {
        toast.error('Choice questions must have at least 2 options');
        return false;
      }
    }

    return true;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      const updatedQuestions = [...allQuestions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        questionText: editedQuestion.questionText,
        questionDetails: editedQuestion.questionDetails,
        skillsTested: editedQuestion.skillsTested,
        ...(editedQuestion.assessmentInstructions && {
          assessmentInstructions: editedQuestion.assessmentInstructions
        }),
        ...((['multiple-answer', 'single-answer', 'true-false'].includes(editedQuestion.type)) && {
          options: editedQuestion.options
        }),
        ...(editedQuestion.codeSnippet && {
          codeSnippet: editedQuestion.codeSnippet
        }),
        ...(editedQuestion.type === 'true-false' && editedQuestion.statement && {
          statement: editedQuestion.statement
        })
      };

      // Use utility function to update questions
      await updateChallengeQuestions(challengeId, updatedQuestions, user);

      toast.success('Question updated successfully!');
      
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Render question type badge
  const renderTypeBadge = () => {
    const typeLabels = {
      'multiple-answer': 'Multiple Choice',
      'single-answer': 'Single Choice',
      'long-text': 'Long Text',
      'short-text': 'Short Text',
      'code-write': 'Code Writing',
      'code-completion': 'Code Completion',
      'code-explanation': 'Code Explanation',
      'code-refactoring': 'Code Refactoring',
      'find-the-bug': 'Find the Bug',
      'security-task': 'Security Task',
      'performance-optimization': 'Performance Optimization',
      'true-false': 'True/False'
    };
    
    return (
      <span className="px-3 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
        {typeLabels[editedQuestion.type] || editedQuestion.type}
      </span>
    );
  };

  // Check if question type requires a code snippet using the centralized helper
  const requiresCodeSnippet = questionTypeRequiresCodeSnippet(editedQuestion.type);

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay with blur */}
      <div 
        className={`fixed inset-0 bg-white transition-all duration-300 ${
          isAnimating ? 'backdrop-blur-sm bg-opacity-40' : 'bg-opacity-0'
        }`}
        onClick={onClose}
      />
      
      {/* Sliding Panel */}
      <div className="fixed inset-y-0 right-0 flex max-w-full">
        <div 
          ref={popupRef}
          className={`w-screen max-w-4xl transform transition-all duration-300 ease-in-out ${
            isAnimating ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col bg-white shadow-2xl overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-20">
              <h2 className="text-2xl font-bold text-gray-900">Edit Challenge Question</h2>
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 overflow-visible">
              {/* Question Type Badge */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Question Type
                </label>
                {renderTypeBadge()}
              </div>

              {/* Skills Tested */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Skills Tested <span className="text-red-500">*</span>
                </label>
                <ComboBoxSelect
                  value={skillsAsOptions}
                  onChange={handleSkillsChange}
                  options={skillsOptions}
                  placeholder="Type to search or add custom skills..."
                  onInputChange={(inputValue, action) => {
                    if (action.action === 'input-change') {
                      if (inputValue && inputValue.length >= 2) {
                        searchSkills(inputValue);
                      } else {
                        clearSkills();
                      }
                    }
                  }}
                  isLoading={isLoadingSkills}
                  onCreateOption={handleCreateSkill}
                  isMulti={true}
                />
              </div>

              {/* Question Text */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Question Text <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editedQuestion.questionText}
                  onChange={handleQuestionTextChange}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
                  placeholder="Enter the question text..."
                />
              </div>

              {/* Question Details */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Question Details (optional)
                </label>
                <textarea
                  value={editedQuestion.questionDetails}
                  onChange={handleQuestionDetailsChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
                  placeholder="Enter additional details, constraints, or requirements..."
                />
              </div>

              {/* Statement - Only for true-false questions */}
              {editedQuestion.type === 'true-false' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Statement (optional)
                  </label>
                  <textarea
                    value={editedQuestion.statement || ''}
                    onChange={handleStatementChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
                    placeholder="Additional statement for true/false question..."
                  />
                </div>
              )}

              {/* Code Snippet - For code questions that need existing code */}
              {requiresCodeSnippet && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Code Snippet <span className="text-red-500">*</span>
                  </label>
                  <CodeEditor
                    question={editedQuestion}
                    value={editedQuestion.codeSnippet || ''}
                    onChange={handleCodeSnippetChange}
                    label="Syntax: "
                  />
                </div>
              )}

              {/* Options - Only for choice questions */}
              {(['multiple-answer', 'single-answer', 'true-false'].includes(editedQuestion.type)) && (
                <div>
                  <InputList
                    label="Options (minimum 2 required)"
                    items={optionsAsItems}
                    onItemsChange={handleOptionsChange}
                    placeholder="Enter an option..."
                  />
                </div>
              )}

              {/* AI Assessment Guidance Section */}
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 space-y-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-purple-900">Custom AI Assessment Guidance</h3>
                  </div>
                  <span className="text-xs text-purple-600 font-medium">(optional)</span>
                </div>
                <p className="text-sm text-purple-700 mb-4">
                  Provide instructions to guide the AI when evaluating candidate responses. Include the ideal solution, assessment criteria, and any context about what to focus on or ignore during evaluation.
                </p>

                {/* Assessment Context - For all questions */}
                <div>
                  <label className="block text-sm font-semibold text-purple-900 mb-3">
                    Assessment Context
                  </label>
                  <textarea
                    value={editedQuestion.assessmentInstructions}
                    onChange={handleAssessmentInstructionsChange}
                    rows={6}
                    className="w-full px-4 py-3 border border-purple-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base bg-white"
                    placeholder="e.g., Focus primarily on the logic. It is okay if the syntax is slightly off or if they miss a semicolon, as long as the refactoring pattern is correct."
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-center gap-4 z-20">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="px-8 py-3 bg-white text-gray-700 border-2 border-gray-300 font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                CANCEL
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-8 py-3 bg-[#33C6B6] text-white font-semibold rounded-lg hover:bg-[#2db5a6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    SAVING...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    SAVE CHANGES
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditChallengeQuestion;
